import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';
import { supabase } from '../../../lib/supabase';

// Simple toast helper (inline, no external dep)
const showToast = (message, type = 'success') => {
  const el = document.createElement('div');
  el.textContent = message;
  el.style.cssText = `
    position:fixed;bottom:24px;right:24px;z-index:9999;
    padding:12px 20px;border-radius:8px;font-size:14px;font-weight:500;
    color:#fff;box-shadow:0 4px 12px rgba(0,0,0,0.15);
    background:${type === 'success' ? '#16a34a' : '#dc2626'};
    transition:opacity 0.3s;
  `;
  document.body?.appendChild(el);
  setTimeout(() => { el.style.opacity = '0'; setTimeout(() => el?.remove(), 300); }, 3000);
};

const DeleteConfirmModal = ({ prof, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
          <Icon name="Trash2" size={20} className="text-red-600" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Elimina account</h2>
      </div>
      <p className="text-sm text-gray-600 mb-6">
        Sei sicuro di voler eliminare questo account? L'utente potrà registrarsi di nuovo con la stessa email, ma questa azione non è reversibile per l'account attuale.
      </p>
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          Annulla
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
        >
          {loading ? (
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
            </svg>
          ) : (
            <Icon name="Trash2" size={14} />
          )}
          Elimina
        </button>
      </div>
    </div>
  </div>
);

const ProfessionistiTable = ({ professionisti, onAction, onDeleteSuccess }) => {
  const [actionLoading, setActionLoading] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // prof object to delete
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();

  const getVerificationBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return badges?.[status] || badges?.pending;
  };

  const getVerificationLabel = (status) => {
    const labels = {
      pending: 'In Attesa',
      approved: 'Approvato',
      rejected: 'Rifiutato'
    };
    return labels?.[status] || status;
  };

  const getAccountStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-yellow-100 text-yellow-800',
      banned: 'bg-red-100 text-red-800'
    };
    return badges?.[status] || badges?.active;
  };

  const getAccountStatusLabel = (status) => {
    const labels = {
      active: 'Attivo',
      suspended: 'Sospeso',
      banned: 'Bannato'
    };
    return labels?.[status] || status;
  };

  const handleAction = async (professionistaId, action) => {
    let confirmMessage = '';
    if (action === 'approve') confirmMessage = 'Sei sicuro di voler approvare questo professionista?';
    else if (action === 'reject') confirmMessage = 'Sei sicuro di voler rifiutare questo professionista?';
    else if (action === 'suspend') confirmMessage = 'Sei sicuro di voler sospendere questo professionista?';
    else if (action === 'ban') confirmMessage = 'Sei sicuro di voler bannare questo professionista?';
    else if (action === 'reactivate') confirmMessage = 'Sei sicuro di voler riattivare questo professionista?';

    if (!window.confirm(confirmMessage)) return;

    setActionLoading(`${professionistaId}-${action}`);
    await onAction(professionistaId, action);
    setActionLoading(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      const { error } = await supabase?.rpc('admin_soft_delete_user', {
        target_id: deleteTarget?.id,
        delete_reason: 'admin_manual_delete'
      });
      if (error) throw error;
      showToast('Account eliminato', 'success');
      setDeleteTarget(null);
      if (typeof onDeleteSuccess === 'function') {
        onDeleteSuccess(deleteTarget?.id);
      }
    } catch (err) {
      showToast(err?.message || 'Errore durante l\'eliminazione', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const EliminaButton = ({ prof }) => (
    <button
      onClick={() => setDeleteTarget(prof)}
      title="Elimina account"
      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
    >
      <Icon name="Trash2" size={13} />
      Elimina
    </button>
  );

  const renderActionButtons = (prof) => {
    const verStatus = prof?.verification_status;
    const accStatus = prof?.account_status;

    // pending verification: Rivedi + Approva + Rifiuta + Elimina
    if (verStatus === 'pending') {
      return (
        <>
          <Button variant="outline" size="sm" iconName="Eye" onClick={() => navigate(`/admin/professionisti/review/${prof?.id}`)}>Rivedi</Button>
          <Button variant="success" size="sm" onClick={() => handleAction(prof?.id, 'approve')} disabled={actionLoading === `${prof?.id}-approve`} loading={actionLoading === `${prof?.id}-approve`}>Approva</Button>
          <Button variant="danger" size="sm" onClick={() => handleAction(prof?.id, 'reject')} disabled={actionLoading === `${prof?.id}-reject`} loading={actionLoading === `${prof?.id}-reject`}>Rifiuta</Button>
          <EliminaButton prof={prof} />
        </>
      );
    }

    // approved + active: Rivedi + Sospendi + Banna + Elimina
    if (verStatus === 'approved' && accStatus === 'active') {
      return (
        <>
          <Button variant="outline" size="sm" iconName="Eye" onClick={() => navigate(`/admin/professionisti/review/${prof?.id}`)}>Rivedi</Button>
          <Button variant="warning" size="sm" onClick={() => handleAction(prof?.id, 'suspend')} disabled={actionLoading === `${prof?.id}-suspend`} loading={actionLoading === `${prof?.id}-suspend`}>Sospendi</Button>
          <Button variant="danger" size="sm" onClick={() => handleAction(prof?.id, 'ban')} disabled={actionLoading === `${prof?.id}-ban`} loading={actionLoading === `${prof?.id}-ban`}>Banna</Button>
          <EliminaButton prof={prof} />
        </>
      );
    }

    // suspended: Rivedi + Riattiva + Banna + Elimina
    if (accStatus === 'suspended') {
      return (
        <>
          <Button variant="outline" size="sm" iconName="Eye" onClick={() => navigate(`/admin/professionisti/review/${prof?.id}`)}>Rivedi</Button>
          <Button variant="success" size="sm" onClick={() => handleAction(prof?.id, 'reactivate')} disabled={actionLoading === `${prof?.id}-reactivate`} loading={actionLoading === `${prof?.id}-reactivate`}>Riattiva</Button>
          <Button variant="danger" size="sm" onClick={() => handleAction(prof?.id, 'ban')} disabled={actionLoading === `${prof?.id}-ban`} loading={actionLoading === `${prof?.id}-ban`}>Banna</Button>
          <EliminaButton prof={prof} />
        </>
      );
    }

    // banned: Rivedi + Riattiva + Elimina
    if (accStatus === 'banned') {
      return (
        <>
          <Button variant="outline" size="sm" iconName="Eye" onClick={() => navigate(`/admin/professionisti/review/${prof?.id}`)}>Rivedi</Button>
          <Button variant="success" size="sm" onClick={() => handleAction(prof?.id, 'reactivate')} disabled={actionLoading === `${prof?.id}-reactivate`} loading={actionLoading === `${prof?.id}-reactivate`}>Riattiva</Button>
          <EliminaButton prof={prof} />
        </>
      );
    }

    return <EliminaButton prof={prof} />;
  };

  if (professionisti?.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <Icon name="Briefcase" size={48} className="mx-auto text-gray-400 mb-4" />
        <p className="text-gray-600">Nessun professionista trovato</p>
      </div>
    );
  }

  return (
    <>
      {deleteTarget && (
        <DeleteConfirmModal
          prof={deleteTarget}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
          loading={deleteLoading}
        />
      )}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-44">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                  Professione
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preventivi inviati
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preventivi accettati
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stato Verifica
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stato Account
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] min-w-[320px]">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {professionisti?.map((prof) => {
                const missingProfession = !prof?.profession || prof?.profession?.trim() === '';
                return (
                  <tr key={prof?.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 w-44">
                      <div className="text-sm font-medium text-gray-900 truncate max-w-[160px]" title={prof?.email}>{prof?.email}</div>
                    </td>
                    <td className="px-4 py-4 w-36">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 truncate max-w-[120px]">{prof?.profession || '-'}</span>
                        {missingProfession && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200 whitespace-nowrap">
                            ⚠ Mancante
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <span className="text-sm font-semibold text-gray-700">{prof?.preventivi_inviati ?? 0}</span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <span className="text-sm font-semibold text-gray-700">{prof?.preventivi_accettati ?? 0}</span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getVerificationBadge(prof?.verification_status)}`}>
                        {getVerificationLabel(prof?.verification_status)}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getAccountStatusBadge(prof?.account_status)}`}>
                        {getAccountStatusLabel(prof?.account_status)}
                      </span>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] min-w-[320px]">
                      <div className="flex items-center justify-end gap-1.5 flex-nowrap">
                        {renderActionButtons(prof)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default ProfessionistiTable;